import { courseIdFromRedirect } from './registrationAuth';

const STORAGE_KEY = 'cloudwise.pendingCourseEnrollment';
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

export interface PendingCourseEnrollment {
  courseId: string;
  redirect: string;
  email: string;
  createdAt: number;
  formId?: string;
}

interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): unknown;
  removeItem(key: string): unknown;
}

export function buildRegistrationEnrollmentRequest(pending: Pick<PendingCourseEnrollment, 'courseId' | 'formId'>) {
  if (pending.formId) {
    return {
      url: '/api/enroll-registration-courses',
      body: { formId: pending.formId },
    };
  }
  return {
    url: '/api/enroll-in-course',
    body: { courseId: pending.courseId },
  };
}

export function canFallbackAfterRegistrationEnrollmentFailure(formId: string | null | undefined) {
  return !formId;
}

export function registrationBundleFailureState(_current: {
  checkingSession: boolean;
  redirecting: boolean;
  activeTab: string;
}) {
  return {
    checkingSession: false,
    redirecting: false,
    bundleEnrollmentFailed: true,
    activeTab: 'signin',
  };
}

export function savePendingCourseEnrollment(
  storage: StorageLike,
  redirect: string | null | undefined,
  email: string,
  now = Date.now(),
  formId?: string | null,
) {
  const courseId = courseIdFromRedirect(redirect);
  const normalizedEmail = email.trim().toLowerCase();
  if (!courseId || !redirect || !normalizedEmail) return false;
  storage.setItem(STORAGE_KEY, JSON.stringify({
    courseId,
    redirect,
    email: normalizedEmail,
    createdAt: now,
    ...(formId ? { formId } : {}),
  }));
  return true;
}

export function readPendingCourseEnrollment(storage: StorageLike, now = Date.now()): PendingCourseEnrollment | null {
  const raw = storage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as PendingCourseEnrollment;
    if (!parsed.courseId || !parsed.redirect || !parsed.email || !parsed.createdAt || now - parsed.createdAt > MAX_AGE_MS) {
      storage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    storage.removeItem(STORAGE_KEY);
    return null;
  }
}

export async function completePendingCourseEnrollment(args: {
  storage: StorageLike;
  signedInEmail: string;
  enroll(pending: PendingCourseEnrollment): Promise<void>;
  now?: number;
}): Promise<PendingCourseEnrollment | null> {
  const pending = readPendingCourseEnrollment(args.storage, args.now);
  if (!pending || pending.email !== args.signedInEmail.trim().toLowerCase()) return null;
  await args.enroll(pending);
  args.storage.removeItem(STORAGE_KEY);
  return pending;
}
